import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import routeShellManifest from "./routeShellManifest.json";

export const PUBLIC_ORIGIN = routeShellManifest.origin;

const defaultMetadata = {
  title: "Urban Unity Network | Local events, services and support",
  description:
    "Find trusted local services, community events, useful places and opportunities across the Isle of Dogs and Tower Hamlets.",
};

const notFoundMetadata = {
  title: "Page not found | Urban Unity Network",
  description: "The requested Urban Unity Network page could not be found.",
};

const routeMetadata = Object.fromEntries(
  routeShellManifest.routes.map((route) => [
    route.path,
    { title: route.title, description: route.description },
  ])
);

const dynamicRouteMetadata = routeShellManifest.dynamicRoutes.map((route) => ({
  pattern: new RegExp(route.regex),
  metadata: route.title && route.description
    ? { title: route.title, description: route.description }
    : defaultMetadata,
}));

function cleanPathname(pathname = "/") {
  return pathname === "/" ? "/" : `/${pathname.replace(/^\/+|\/+$/g, "")}`;
}

export function canonicalUrlForPath(pathname = "/") {
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return `${PUBLIC_ORIGIN}${path === "/" ? "/" : path.replace(/\/+$/, "")}`;
}

export function metadataForPath(pathname = "/") {
  const cleanPath = cleanPathname(pathname);
  if (routeMetadata[cleanPath]) return routeMetadata[cleanPath];
  const dynamicRoute = dynamicRouteMetadata.find(({ pattern }) => pattern.test(cleanPath));
  if (dynamicRoute) return dynamicRoute.metadata;
  return notFoundMetadata;
}

export function isPrivatePath(pathname = "/") {
  return ["/admin", "/admin-login", "/profile", "/insights", "/phoenix-hub/booking-success"].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isKnownRoute(pathname = "/") {
  const cleanPath = cleanPathname(pathname);
  return Boolean(
    routeMetadata[cleanPath] ||
      dynamicRouteMetadata.some(({ pattern }) => pattern.test(cleanPath))
  );
}

function ensureMeta(selector, attributes) {
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  return element;
}

export function applyDocumentMetadata({
  title,
  description,
  canonicalUrl,
  robots = "index,follow",
  image = `${PUBLIC_ORIGIN}/logo.png`,
  type = "website",
}) {
  document.title = title;

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = canonicalUrl;

  ensureMeta('meta[name="description"]', { name: "description", content: description });
  ensureMeta('meta[name="robots"]', { name: "robots", content: robots });
  ensureMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
  ensureMeta('meta[property="og:title"]', { property: "og:title", content: title });
  ensureMeta('meta[property="og:description"]', { property: "og:description", content: description });
  ensureMeta('meta[property="og:type"]', { property: "og:type", content: type });
  ensureMeta('meta[property="og:image"]', { property: "og:image", content: image });
  ensureMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  ensureMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
  ensureMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
  ensureMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
}

export default function CanonicalUrl() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const canonicalUrl = canonicalUrlForPath(pathname);
    const metadata = metadataForPath(pathname);
    const privatePage = isPrivatePath(pathname) || !isKnownRoute(pathname);

    applyDocumentMetadata({
      ...metadata,
      canonicalUrl,
      robots: privatePage ? "noindex,nofollow" : "index,follow",
    });
  }, [pathname]);

  useEffect(() => {
    if (!hash) return;
    const scrollToTarget = () => {
      const target = document.getElementById(hash.slice(1));
      if (target) target.scrollIntoView({ block: "start" });
    };

    // Firestore content can change the page height after the first render.
    // Re-align the requested section while that initial content settles.
    const timers = [0, 400, 1200, 2500].map((delay) =>
      window.setTimeout(scrollToTarget, delay)
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [hash, pathname]);

  return null;
}
